import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';

import { useOrderedProducts } from 'hooks/orderForms/useOrderedProducts';
import { logError } from 'services/logging/logging';
import { useOrderFormStore } from 'stores/orderForm/orderForm';
import { formatCurrency } from 'support/helpers/currency/currency';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { OrderFormsRoutes } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { Button } from 'components/Form/Button';

export const OrderFormCheckoutButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { supplierSlug } = useParams<RouteParams<OrderFormsRoutes.root>>();
  const { subtotal, minAmount, currency } = useOrderFormStore((state) => ({
    subtotal: state.subtotal,
    minAmount: state.minAmount,
    currency: state.currency,
  }));
  const orderedProducts = useOrderedProducts(supplierSlug as string);
  const orderedQuantities = Object.values(orderedProducts);
  const hasQuantity = orderedQuantities.some((ordered) => ordered.quantity);

  const amountMissing = minAmount ? minAmount - subtotal : 0;
  const minAmountMet = amountMissing <= 0 && hasQuantity;

  const handleCheckoutClick = () => {
    if (!supplierSlug) {
      logError('Order Forms: supplierSlug is missing from URL params on the checkout button');
      return;
    }
    navigate(routeToPage(OrderFormsRoutes.checkout, { supplierSlug }));
  };

  return (
    <Tooltip placement="bottom">
      <TooltipTrigger>
        <Button
          analyticsId="order-form-checkout-button"
          size="large"
          RightIcon={ArrowLongRightIcon}
          disabled={!minAmountMet}
          onClick={handleCheckoutClick}
        >
          {t('orderForms:form.checkoutButton.label')}
        </Button>
      </TooltipTrigger>
      {!minAmountMet && minAmount ? (
        <TooltipContent>
          {t('orderForms:form.checkoutButton.minAmountTooltip', {
            minAmount: formatCurrency(minAmount, currency),
            amountMissing: formatCurrency(amountMissing, currency),
          })}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
};
